import { z } from 'zod'
import { httpUrlSchema } from './url.js'
import { horairesSchema } from './horaires.js'

export const socialLinkSchema = z.object({
  label: z.string().trim().min(1, 'Le nom du réseau est obligatoire'),
  url: httpUrlSchema,
})

export const practicalInfoSchema = z.object({
  address: z.string().trim().min(1, "L'adresse est obligatoire"),
  mapsUrl: httpUrlSchema,
  phone: z.string().trim().min(1, 'Le numéro de téléphone est obligatoire'),
  email: z.string().email('Adresse électronique invalide'),
  openingLines: z
    .array(z.string().trim().min(1, "Cette ligne d'horaires ne peut pas être vide"))
    .min(1, "Au moins une ligne d'horaires est obligatoire"),
  /**
   * Les horaires structurés (issue #158), qui portent de vraies heures là où
   * `openingLines` ci-dessus n'a que des phrases.
   *
   * **Optionnel, et il doit le rester tant qu'aucune plage n'est saisie.** Les
   * vraies heures doivent venir de l'institut ; en inventer ferait afficher
   * « Ouvert » par Google quand la porte est close. Absent, tout se comporte
   * comme avant : le pied de page lit `openingLines`, et le balisage
   * Schema.org se tait sur les horaires.
   *
   * Son ajout ne demande aucune migration : les réglages sont stockés en JSONB
   * et validés par ce schéma, et une valeur déjà en base reste valide.
   */
  openingHours: horairesSchema.optional(),
  socialLinks: z.array(socialLinkSchema),
  partnersLabel: z.string(),
  partnersUrl: z.string(),
  copyright: z.string().trim().min(1, 'Le copyright est obligatoire'),
})

export type PracticalInfo = z.infer<typeof practicalInfoSchema>
export type SocialLink = z.infer<typeof socialLinkSchema>

export const DEFAULT_PRACTICAL_INFO: PracticalInfo = {
  address: '1bis, Rue Saint-Vincent 89290 Auxerre',
  mapsUrl: 'https://maps.google.com/?q=1bis,+Rue+Saint-Vincent+89290+Auxerre',
  phone: '06 79 33 33 64',
  email: 'contact-vaux@azoya-spa.com',
  openingLines: [
    'Ouvert 5/7 jours',
    'Lundi - Mardi - Mercredi - Vendredi - Samedi',
    'Jeudi - Dimanche : Fermé',
  ],
  // Les icônes du pied de page pointent aujourd'hui sur href="#". Ces adresses
  // sont à confirmer avec l'institut avant la mise en production.
  socialLinks: [
    { label: 'Instagram', url: 'https://www.instagram.com/' },
    { label: 'Facebook', url: 'https://www.facebook.com/' },
  ],
  partnersLabel: 'Découvrir →',
  // Vide tant que la page /partenaires n'existe pas : le composant masque le
  // bloc plutôt que de renvoyer vers la page 404, comme aujourd'hui.
  partnersUrl: '',
  copyright: '© 2026 Azoya Spa Vaux',
}
